import React from 'react';
import { RedoOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import moment from 'moment-timezone';

import API from 'utils/request';

export const EXCLUDED_FILTERS_FOR_AUDIENCE_SIZE = [
  'loyalty_tier',
  'earned_badges',
  'earned_rewards',
  'campaign_enrolment',
  'account_confirmed_at',
  'leaderboard_position',
  'loyalty_points_balance'
];

interface AudienceSizeColumnProps {
  record: any;
  recalculatingAudiences: Set<number>;
  erroredAudiences: Set<number>;
  onRecalculateStart: (audienceId: number) => void;
  onRecalculateEnd: (audienceId: number, isTimeout?: boolean) => void;
}

const AudienceSizeColumn: React.FC<AudienceSizeColumnProps> = ({
  record,
  recalculatingAudiences,
  erroredAudiences,
  onRecalculateStart,
  onRecalculateEnd
}) => {
  const isNotEligibleForAudienceSizeCalculation = (record: any) => {
    if (!record.filters) return false;
    return EXCLUDED_FILTERS_FOR_AUDIENCE_SIZE.some(
      filter =>
        record.filters.hasOwnProperty(filter) &&
        record.filters[filter] !== null &&
        record.filters[filter] !== undefined
    );
  };

  const hasNoPreviousAudienceSizeCalculation = (record: any) => {
    return record.last_counted_at === null && record.user_account_counts === null;
  };

  interface GenerateCountResponse {
    data: {
      message?: string;
    };
  }

  const onRecalculateAudience = async (record: any) => {
    onRecalculateStart(record.id);

    message.info(`Recalculating Audience ID: ${record.id} Size`);

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('API timeout!')), 5 * 60 * 1000); // 5 minutes timeout
      });

      const response = (await Promise.race([
        API.post<GenerateCountResponse>(`/v4/dash/v4_audiences/${record.id}/generate_count`),
        timeoutPromise
      ])) as GenerateCountResponse;

      if (response.data && response.data.message.includes('Calculation in progress')) {
        message.success(
          'Calculation in progress. Please refresh the page after some time to see the updated count!'
        );
        return;
      }
    } catch (error: any) {
      onRecalculateEnd(record.id, true);
      message.error('Error recalculating audience size!');
    }
  };

  const isNotEligible = isNotEligibleForAudienceSizeCalculation(record);
  const hasNoPreviousCalculation = hasNoPreviousAudienceSizeCalculation(record);
  const showButton = !isNotEligible || !hasNoPreviousCalculation;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {(() => {
        const mutedStyle = { color: '#888' };
        const mutedLargeStyle = { ...mutedStyle, fontSize: 16 };
        const mutedSmallStyle = { ...mutedStyle, fontSize: 12, marginTop: 2 };
        const mainNumberStyle = { color: '#333', fontWeight: 500 };
        const italicStyle = { ...mutedStyle, fontStyle: 'italic' };

        return (
          <div>
            {isNotEligible ? (
              <div style={mutedLargeStyle}>- -</div>
            ) : hasNoPreviousCalculation ? (
              <div style={mainNumberStyle}>0</div>
            ) : recalculatingAudiences.has(record.id) ? (
              <div style={italicStyle}>recalculating...</div>
            ) : erroredAudiences.has(record.id) ? (
              <div style={mutedLargeStyle}>– –</div>
            ) : (
              <>
                <div style={mainNumberStyle} data-testid="audience-count">
                  {typeof record.user_account_counts === 'number' && record.user_account_counts > 0
                    ? new Intl.NumberFormat('en-US', {
                        maximumFractionDigits: 1
                      }).format(record.user_account_counts)
                    : '-'}
                </div>
                {(record.last_counted_at && (
                  <div style={mutedSmallStyle} data-testid="audience-timestamp">
                    at {moment(record.last_counted_at).format('DD MMM YYYY HH:mm')}
                  </div>
                )) || (
                  <div style={mutedSmallStyle} data-testid="audience-timestamp">
                    - -
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}
      {showButton && (
        <Button
          type="primary"
          size="small"
          icon={<RedoOutlined />}
          onClick={e => {
            e.stopPropagation();
            onRecalculateAudience(record);
          }}
          style={{
            marginLeft: 8,
            borderRadius: '6px'
          }}
          title="Refresh audience data"
          loading={recalculatingAudiences.has(record.id)}
          disabled={recalculatingAudiences.has(record.id)}
        />
      )}
    </div>
  );
};

export default AudienceSizeColumn;
